import React from 'react';

import { useIntl } from 'react-intl';

import { useCreatePayStationApiKey } from 'src/features/payment/api/useCreatePayStationApiKey';
import { LIST_PAY_STATIONS_RESOURCE } from 'src/features/payment/api/useListPayStations';
import { paymentsMessages } from 'src/features/payment/utils/messages';
import { PayStation } from 'src/features/payment/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';

interface CreatePayStationApiKeyFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedPayStation: PayStation;
    children?: React.ReactNode;
}

export default function CreatePayStationApiKeyFormModal<TData>({
    selectedPayStation,
    children,
}: CreatePayStationApiKeyFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [apiKey, setApiKey] = React.useState<string | null>(null);
    const { formatMessage } = useIntl();

    const { parkingId } = useParking();
    const { mutate: createPayStationApiKey } = useCreatePayStationApiKey({
        onSuccess: ({ api_key }) => {
            setApiKey(api_key);
            void queryClient.invalidateQueries({ queryKey: [LIST_PAY_STATIONS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(paymentsMessages.deletePayStationSuccess),
                description: formatMessage(paymentsMessages.deletePayStationSuccessDescription),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <>
            <ConfirmationModal
                isModalOpen={isModalOpen}
                setIsModalOpen={setIsModalOpen}
                dialogDetails={{
                    title: paymentsMessages.generateNewApiKey,
                    subTitle: paymentsMessages.generateNewApiKeyDescription,
                    icon: ICON_MAP.ADD,
                    confirmationMessage: paymentsMessages.confirmGenerateNewApiKey,
                }}
                callbackButton={{
                    variant: 'default',
                    buttonTitle: paymentsMessages.generateNewApiKey,
                    callbackFn: () => {
                        createPayStationApiKey({
                            parkingId,
                            payStationId: selectedPayStation.id,
                        });
                        setIsModalOpen(false);
                    },
                }}
            >
                {children}
            </ConfirmationModal>
            <Dialog open={!!apiKey} onOpenChange={() => setApiKey(null)}>
                <DialogContent>
                    <DialogHeader>
                        <div className="flex gap-2">
                            <SquareIcon icon={ICON_MAP.DANGER} />
                            <DialogTitle>{formatMessage(paymentsMessages.copyApiKey)}</DialogTitle>
                        </div>
                    </DialogHeader>
                    <DialogSeparator />
                    <DialogBody>
                        <p className="text-gray-500">
                            {formatMessage(paymentsMessages.copyGeneratedApiKey, {
                                key: apiKey,
                                b: (chunks: React.ReactNode[]) => <b className="font-semibold">{chunks}</b>,
                            })}
                        </p>
                    </DialogBody>
                    <DialogSeparator />
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button type="button" className="flex-1">
                                {formatMessage(paymentsMessages.continue)}
                            </Button>
                        </DialogClose>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
